import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Handshake, Phone, ChevronDown, Info, ShieldCheck, TrendingUp, Scale } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import Reveal from "@/components/site/Reveal";
import GuestDetailsDialog from "@/components/site/GuestDetailsDialog";
import { getGuest } from "@/lib/guest";
import { trackEvent } from "@/lib/settings";
import { usePageContent } from "@/lib/content";
import { mergePM } from "@/lib/partnershipContent";
import { computeComparison, SCENARIOS, bandLabel } from "@/lib/partnershipEngine";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(n || 0))}`;
const MODEL_KEYS = ["fixed", "management", "mg"];
const MODEL_ICON = { fixed: ShieldCheck, management: TrendingUp, mg: Scale };
const PREF_TO_MODEL = { guaranteed: "fixed", maximum: "management", balanced: "mg" };
const TYPE_LABELS = { villa_event: "Villa + Event Space", rooms_event: "Rooms + Event Space", villa_rooms_event: "Villa + Rooms + Event Space" };

function HowCalculated({ lines }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3">
      <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-clay font-medium hover:text-ink transition-colors" data-testid="how-calculated-btn">
        <Info className="w-3.5 h-3.5" /> How was this calculated? <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 bg-bone px-4 py-3 space-y-1" data-testid="how-calculated-panel">
          {lines.map((l, i) => (
            <div key={i} className={`flex justify-between gap-4 text-xs ${i === lines.length - 1 ? "font-medium border-t border-ink/10 pt-1.5 mt-1.5" : "text-graphite"}`}>
              <span>{l[0]}</span><span className="whitespace-nowrap tabular-nums">{l[1]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PartnershipCompare({ r, propertyName = "", propertyType = "", rooms = 0, stage = "", guestMeta = {} }) {
  const { content } = usePageContent("partnership_models");
  const cfg = useMemo(() => mergePM(content), [content]);
  const [scenario, setScenario] = useState("projected");
  const [period, setPeriod] = useState("annual");
  const [pref, setPref] = useState(null);
  const [detail, setDetail] = useState(null);
  const [guestOpen, setGuestOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const calc = useMemo(() => computeComparison(r || 0, cfg, stage), [r, cfg, stage]);
  const div = period === "monthly" ? 12 : 1;
  const fmt = (n) => inr(n / div);
  const perLabel = period === "monthly" ? "/month" : "/year";
  const suggested = pref ? PREF_TO_MODEL[pref] : null;
  const c = cfg.content;
  const tr = cfg.transparency;
  const stageOpt = (cfg.stages?.options || []).find((o) => o.id === stage);
  const bands = calc.stageBands;
  const timeline = bands ? bands.map((b, i) => `${bandLabel(bands, i)}: ${b.pct}%`).join("  →  ") : null;
  const stageNote = stageOpt && cfg.stages?.enabled !== false
    ? (timeline || (cfg.stages.note_template || "").replace("{stage}", stageOpt.label.toLowerCase()).replace("{range}", `${stageOpt.range_min}%–${stageOpt.range_max}%`))
    : null;

  const income = (m) => calc.ownerIncome[m][scenario];
  const maxIncome = Math.max(1, ...MODEL_KEYS.map((m) => Math.max(...SCENARIOS.map((s) => calc.ownerIncome[m][s]))));

  const calcLines = (m) => {
    const rev = calc.revs[scenario];
    if (m === "fixed") {
      return [["Agreed annual fixed rent", inr(calc.rent)], ["Operating expenses paid by you", inr(0)], ["Your estimated annual income", inr(calc.rent)]];
    }
    if (m === "management") {
      return [
        [`${cfg.scenarios[scenario].label} revenue`, inr(rev)],
        ["Less estimated property operating expenses", `− ${inr(calc.opex[scenario])}`],
        ["Less Happy Retreats management fee", `− ${inr(calc.mgmtFee[scenario])}`],
        ["Your estimated annual income", inr(calc.ownerIncome.management[scenario])],
      ];
    }
    return [
      ["Guaranteed minimum (protected)", inr(calc.minGuarantee)],
      [`Revenue share (${calc.sharePctEff}%) at ${cfg.scenarios[scenario].label.toLowerCase()} revenue`, inr(calc.mgShareAmt[scenario])],
      ["You receive the higher amount", inr(calc.mgPayout[scenario])],
    ];
  };

  const downloadPdf = async (guest) => {
    setDownloading(true);
    try {
      const payload = {
        ...guest, ...guestMeta,
        property_name: propertyName, property_type: propertyType, rooms: Number(rooms) || 0,
        property_stage: stageOpt ? stageOpt.label : stage,
        projected_revenue: calc.R,
        revs: calc.revs,
        rent: calc.rent, min_guarantee: calc.minGuarantee,
        owner_income: calc.ownerIncome, mgmt_fee: calc.mgmtFee, mg_payout: calc.mgPayout, opex: calc.opex,
        opp_cost: calc.oppCost, opex_pct: calc.opexPctTotal,
        assumptions: (cfg.expenses || []).filter((e) => e.active !== false).map((e) => ({ name: e.name, type: e.type, pct: e.pct, fixed_amount: e.fixed_amount, hybrid_fixed: e.hybrid_fixed, hybrid_pct: e.hybrid_pct })),
        key_pcts: {
          conservative: cfg.scenarios.conservative.multiplier, high: cfg.scenarios.high.multiplier,
          rent_pct: calc.rentPctEff, fee_pct: cfg.management.fee_pct,
          mg_pct: cfg.mg_share.mg_pct, share_pct: calc.sharePctEff, central_pct: cfg.central_support_pct,
        },
        stage_schedule: bands ? bands.map((b, i) => ({ label: bandLabel(bands, i), pct: Number(b.pct) || 0 })) : [],
        readiness_note: bands ? (cfg.stages.readiness_note || "") : "",
      };
      const resp = await api.post("/public/partnership-pdf", payload, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Happy-Retreats-Partnership-Comparison.pdf";
      a.click();
      URL.revokeObjectURL(url);
      trackEvent("partnership_comparison_pdf", { property: propertyName, revenue: Math.round(calc.R) });
      toast.success("Your comparison report is downloading");
    } catch {
      toast.error("Could not prepare the report — please try again");
    } finally {
      setDownloading(false);
    }
  };

  const requestDownload = () => {
    if (!calc.R) { toast.error("Add your revenue details above first"); return; }
    const g = getGuest();
    if (g) downloadPdf(g); else setGuestOpen(true);
  };

  if (!r) return null;

  return (
    <section className="bg-bone border-t border-ink/10" data-testid="partnership-compare" id="compare-models">
      <div className="container-x py-14 md:py-20">
        {/* Top section */}
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="overline text-clay">Partnership structures</div>
              <h2 className="font-display text-3xl md:text-4xl font-light mt-3">{c.heading}</h2>
              <p className="text-sm text-graphite mt-3 max-w-2xl leading-relaxed">{c.intro}</p>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-[0.14em] text-graphite">{propertyName || "Your property"}{propertyType ? ` · ${TYPE_LABELS[propertyType] || propertyType}` : ""}{rooms ? ` · ${rooms} rooms` : ""}</div>
              <div className="font-display text-2xl mt-1" data-testid="pc-projected-revenue">{inr(calc.R)} <span className="text-sm text-graphite">projected /year</span></div>
              <div className="text-xs text-graphite">{inr(calc.R / 12)} monthly equivalent</div>
            </div>
          </div>
        </Reveal>

        {/* Toggles */}
        <div className="mt-8 flex flex-wrap items-center gap-4">
          <div className="flex border border-ink/15 bg-white" data-testid="pc-scenario-toggle">
            {SCENARIOS.map((s) => (
              <button key={s} type="button" onClick={() => setScenario(s)}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-medium transition-colors ${scenario === s ? "bg-ink text-ivory" : "text-graphite hover:text-ink"}`}
                data-testid={`pc-scenario-${s}`}>
                {cfg.scenarios[s].label}
              </button>
            ))}
          </div>
          <div className="flex border border-ink/15 bg-white" data-testid="pc-period-toggle">
            {["annual", "monthly"].map((p) => (
              <button key={p} type="button" onClick={() => setPeriod(p)}
                className={`px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-medium transition-colors ${period === p ? "bg-ink text-ivory" : "text-graphite hover:text-ink"}`}
                data-testid={`pc-period-${p}`}>{p}</button>
            ))}
          </div>
          <span className="text-xs text-graphite">{cfg.scenarios[scenario].label} revenue: <strong>{inr(calc.revs[scenario])}</strong>/year</span>
        </div>

        {stageNote && (
          <div className="mt-4 bg-white border border-clay/40 px-4 py-3 max-w-3xl" data-testid="pc-stage-note">
            <div className="text-xs text-graphite">
              <span className="text-clay font-medium uppercase tracking-[0.14em] text-[10px] mr-2">{stageOpt.label}</span>
              {timeline ? <>Your offer grows with the partnership — <strong className="text-ink">{timeline}</strong></> : stageNote}
            </div>
            {timeline && cfg.stages.readiness_note && (
              <div className="mt-1.5 text-[11px] text-graphite/80 leading-relaxed" data-testid="pc-readiness-note">{cfg.stages.readiness_note}</div>
            )}
          </div>
        )}

        {/* Preference question */}
        <div className="mt-8 border border-ink/10 bg-white p-5" data-testid="pc-preference">
          <div className="text-[11px] uppercase tracking-[0.14em] text-graphite font-medium">{c.preference_question}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(c.preference_options).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setPref(pref === k ? null : k)}
                className={`px-4 py-2.5 text-xs border transition-colors ${pref === k ? "border-ink bg-ink text-ivory" : "border-ink/15 hover:border-ink/40"}`}
                data-testid={`pc-pref-${k}`}>{label}</button>
            ))}
          </div>
        </div>

        {/* Model cards */}
        <div className="mt-8 grid md:grid-cols-3 gap-5">
          {MODEL_KEYS.map((m) => {
            const md = cfg.models[m];
            const Icon = MODEL_ICON[m];
            const isSuggested = suggested === m;
            return (
              <Reveal key={m}>
                <div className={`relative bg-white border p-6 h-full flex flex-col transition-all ${isSuggested ? "border-clay shadow-[0_8px_30px_rgba(158,123,79,0.18)]" : "border-ink/10"}`} data-testid={`pc-card-${m}`}>
                  {isSuggested && <div className="absolute -top-3 left-5 bg-clay text-white text-[10px] uppercase tracking-[0.18em] px-3 py-1" data-testid="pc-suggested-badge">{c.suggested_label}</div>}
                  <div className="flex items-center justify-between">
                    <Icon className="w-5 h-5 text-clay" />
                    <span className={`text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 border ${m === "management" ? "border-amber-500/50 text-amber-700 bg-amber-50" : m === "fixed" ? "border-emerald-500/40 text-emerald-700 bg-emerald-50" : "border-ink/20 text-graphite bg-bone"}`}>{md.badge}</span>
                  </div>
                  <h3 className="font-display text-xl mt-4 leading-snug">{md.name}</h3>
                  <p className="text-xs text-graphite mt-2 leading-relaxed">{md.headline}</p>
                  <div className="mt-5 border-t border-ink/10 pt-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-clay font-medium">Your estimated income</div>
                    <div className="font-display text-3xl mt-1.5" data-testid={`pc-income-${m}`}>{fmt(income(m))}<span className="text-sm text-graphite ml-1">{perLabel}</span></div>
                    <div className="text-[11px] text-graphite mt-1">{period === "monthly" ? `${inr(income(m))} annually` : `${inr(income(m) / 12)} monthly equivalent`}</div>
                    {bands && m !== "management" && (
                      <div className="mt-2 text-[10px] text-clay font-medium leading-relaxed" data-testid={`pc-card-timeline-${m}`}>{timeline}</div>
                    )}
                  </div>
                  <div className="mt-4 space-y-2 text-xs flex-1">
                    <div className="flex justify-between gap-3"><span className="text-graphite">Risk level</span><span className="font-medium">{md.risk_label}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-graphite">Income type</span><span className="font-medium text-right">{md.income_type}</span></div>
                    <div className="flex justify-between gap-3"><span className="text-graphite">Operating expenses paid by</span><span className="font-medium">{md.expenses_paid_by}</span></div>
                    <div className="flex gap-2 pt-1.5 text-emerald-800"><span>✓</span><span>{md.key_benefit}</span></div>
                    <div className="flex gap-2 text-graphite"><span>·</span><span>{md.trade_off}</span></div>
                  </div>
                  <button type="button" onClick={() => setDetail(m)}
                    className="mt-5 w-full border border-ink/20 px-4 py-3 text-[11px] uppercase tracking-[0.18em] font-medium hover:bg-ink hover:text-ivory transition-colors"
                    data-testid={`pc-detail-btn-${m}`}>View detailed calculation</button>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Where every rupee goes */}
        <Reveal>
          <div className="mt-10 bg-white border border-ink/10 p-6 md:p-7" data-testid="pc-allocation">
            <h3 className="font-display text-xl md:text-2xl font-light">{tr.heading}</h3>
            <p className="text-xs text-graphite mt-2 max-w-3xl leading-relaxed">{tr.intro}</p>
            <div className="mt-6 space-y-7">
              {MODEL_KEYS.map((m) => {
                const rev = calc.revs[scenario];
                let segs;
                if (m === "management") {
                  segs = [
                    { label: tr.expenses_label + " (funded by you)", value: calc.opex[scenario], cls: "bg-ink/25" },
                    { label: tr.fee_label, value: calc.mgmtFee[scenario], cls: "bg-clay" },
                    { label: tr.owner_label, value: calc.ownerIncome.management[scenario], cls: "bg-emerald-600/80" },
                  ];
                } else {
                  const ownerPay = m === "fixed" ? calc.rent : calc.mgPayout[scenario];
                  const hrShare = rev - calc.opex[scenario] - ownerPay;
                  segs = [{ label: tr.expenses_label + " (funded by Happy Retreats)", value: calc.opex[scenario], cls: "bg-ink/25" }];
                  if (tr.show_hr_margin) {
                    segs.push({ label: tr.central_label, value: calc.central[scenario], cls: "bg-clay/60" });
                    segs.push({ label: tr.margin_label, value: hrShare - calc.central[scenario], cls: "bg-clay" });
                  } else {
                    segs.push({ label: tr.hr_label, value: hrShare, cls: "bg-clay" });
                  }
                  segs.push({ label: tr.owner_label, value: ownerPay, cls: "bg-emerald-600/80" });
                }
                const total = segs.reduce((a, s) => a + Math.max(0, s.value), 0) || 1;
                return (
                  <div key={m} data-testid={`pc-allocation-${m}`}>
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="text-sm font-medium">{cfg.models[m].name}</span>
                      <span className="text-[11px] text-graphite">{cfg.scenarios[scenario].label} revenue · {inr(rev)}</span>
                    </div>
                    <div className="mt-2.5 flex h-5 overflow-hidden rounded-sm">
                      {segs.map((s, i) => (
                        <div key={i} className={`${s.cls} transition-all duration-500`} style={{ width: `${(Math.max(0, s.value) / total) * 100}%` }} title={`${s.label}: ${inr(s.value)}`} />
                      ))}
                    </div>
                    <div className="mt-2.5 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {segs.map((s, i) => (
                        <div key={i} className="flex items-center justify-between gap-3 text-xs">
                          <span className="flex items-center gap-2 text-graphite"><span className={`inline-block w-2.5 h-2.5 rounded-sm shrink-0 ${s.cls}`} />{s.label}</span>
                          <span className="whitespace-nowrap tabular-nums font-medium" data-testid={`pc-alloc-${m}-${i}`}>{inr(s.value)} <span className="text-graphite font-normal">· {rev ? ((s.value / rev) * 100).toFixed(1) : 0}%</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Itemised expense breakdown */}
            <div className="mt-7 border-t border-ink/10 pt-5">
              <button type="button" onClick={() => setBreakdownOpen(!breakdownOpen)}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-clay font-medium hover:text-ink transition-colors"
                data-testid="pc-breakdown-toggle">
                <Info className="w-3.5 h-3.5" /> {tr.breakdown_label} <ChevronDown className={`w-3 h-3 transition-transform ${breakdownOpen ? "rotate-180" : ""}`} />
              </button>
              {breakdownOpen && (
                <div className="mt-4" data-testid="pc-breakdown">
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-2.5">
                    {calc.expenseRows[scenario].map((e, i) => (
                      <div key={e.id || i} className="flex items-start justify-between gap-4 text-xs border-b border-ink/5 pb-2">
                        <div>
                          <div className="font-medium">{e.name}</div>
                          {e.desc && <div className="text-graphite text-[11px] mt-0.5">{e.desc}</div>}
                        </div>
                        <div className="text-right whitespace-nowrap">
                          <div className="tabular-nums font-medium">{inr(e.amount)}</div>
                          <div className="text-[10px] text-graphite">{e.type === "fixed" ? "fixed" : e.type === "hybrid" ? `fixed + ${e.hybrid_pct}%` : `${e.pct}% of revenue`}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex items-baseline justify-between text-sm font-medium">
                    <span>Total estimated operating expenses · {cfg.scenarios[scenario].label.toLowerCase()}</span>
                    <span className="tabular-nums" data-testid="pc-breakdown-total">{inr(calc.opex[scenario])} · {calc.revs[scenario] ? ((calc.opex[scenario] / calc.revs[scenario]) * 100).toFixed(1) : 0}%</span>
                  </div>
                  <p className="mt-3 text-[11px] text-graphite/80 leading-relaxed max-w-3xl">{tr.breakdown_note}</p>
                </div>
              )}
            </div>
          </div>
        </Reveal>

        {/* Bar chart */}
        <Reveal>
          <div className="mt-10 bg-white border border-ink/10 p-6" data-testid="pc-chart">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <h3 className="font-display text-xl font-light">Owner earnings — side by side</h3>
              <span className="text-xs text-graphite">{cfg.scenarios[scenario].label} scenario · {period}</span>
            </div>
            <div className="mt-6 space-y-4">
              {MODEL_KEYS.map((m) => {
                const v = income(m);
                const w = Math.max(2, (Math.max(0, v) / maxIncome) * 100);
                return (
                  <div key={m}>
                    <div className="flex justify-between text-xs mb-1.5"><span>{cfg.models[m].name}</span><span className="font-medium tabular-nums" data-testid={`pc-bar-value-${m}`}>{fmt(v)}{perLabel}</span></div>
                    <div className="h-3 bg-bone overflow-hidden">
                      <div className={`h-full transition-all duration-500 ${v < 0 ? "bg-red-300" : m === "fixed" ? "bg-emerald-600/70" : m === "management" ? "bg-clay" : "bg-ink/70"}`} style={{ width: `${w}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        {/* Opportunity cost panel */}
        <Reveal>
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            <div className="bg-ink text-ivory p-6" data-testid="pc-opportunity-cost">
              <div className="overline text-ivory/60">Fixed rental — the trade-off, transparently</div>
              <div className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between gap-3"><span className="text-ivory/70">Guaranteed annual income</span><span className="font-medium tabular-nums">{inr(calc.rent)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-ivory/70">Potential upside under management</span><span className="font-medium tabular-nums text-emerald-300" data-testid="pc-opp-mgmt">{calc.oppCost.mgmt_projected > 0 ? `+ ${inr(calc.oppCost.mgmt_projected)}` : inr(0)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-ivory/70">Potential upside under revenue share</span><span className="font-medium tabular-nums text-emerald-300" data-testid="pc-opp-mg">{calc.oppCost.mg_projected > 0 ? `+ ${inr(calc.oppCost.mg_projected)}` : inr(0)}</span></div>
                <div className="flex justify-between gap-3"><span className="text-ivory/70">At high performance — management</span><span className="tabular-nums text-ivory/80">{calc.oppCost.mgmt_high > 0 ? `+ ${inr(calc.oppCost.mgmt_high)}` : inr(0)}</span></div>
              </div>
              <p className="mt-4 text-[11px] text-ivory/50 leading-relaxed">{c.upside_phrase}. {c.protection_phrase} — the operating risk stays with Happy Retreats.</p>
            </div>
            <div className="bg-white border border-ink/10 p-6">
              <div className="overline text-clay">{cfg.models.fixed.gains_title} / {cfg.models.fixed.gives_title}</div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-xs">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 font-medium mb-2">{cfg.models.fixed.gains_title}</div>
                  <ul className="space-y-1.5">{cfg.models.fixed.gains.map((g, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">✓</span><span>{g}</span></li>)}</ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-graphite font-medium mb-2">{cfg.models.fixed.gives_title}</div>
                  <ul className="space-y-1.5 text-graphite">{cfg.models.fixed.gives.map((g, i) => <li key={i} className="flex gap-2"><span>·</span><span>{g}</span></li>)}</ul>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Comparison table */}
        <Reveal>
          <div className="mt-10 bg-white border border-ink/10 overflow-x-auto" data-testid="pc-comparison-table">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="bg-ink text-ivory">
                  <th className="text-left px-4 py-3 font-medium text-[10px] uppercase tracking-[0.16em]">Parameter</th>
                  {MODEL_KEYS.map((m) => <th key={m} className="text-left px-4 py-3 font-medium text-[10px] uppercase tracking-[0.16em]">{cfg.models[m].name}</th>)}
                </tr>
              </thead>
              <tbody>
                {cfg.comparison_rows.map((row, i) => (
                  <tr key={i} className={i % 2 ? "bg-bone/60" : ""}>
                    <td className="px-4 py-2.5 text-graphite">{row.label}</td>
                    <td className="px-4 py-2.5">{row.fixed}</td>
                    <td className="px-4 py-2.5">{row.management}</td>
                    <td className="px-4 py-2.5">{row.mg}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-ink/20 font-medium">
                  <td className="px-4 py-3">Estimated owner income · {cfg.scenarios[scenario].label.toLowerCase()} ({period})</td>
                  {MODEL_KEYS.map((m) => <td key={m} className="px-4 py-3 tabular-nums" data-testid={`pc-table-income-${m}`}>{fmt(income(m))}</td>)}
                </tr>
              </tbody>
            </table>
          </div>
        </Reveal>

        {/* Disclaimer */}
        <p className="mt-6 text-[11px] text-graphite/70 leading-relaxed max-w-4xl" data-testid="pc-disclaimer">{c.disclaimer}</p>

        {/* Final CTA */}
        <Reveal>
          <div className="mt-10 bg-ink text-ivory p-8 md:p-10" data-testid="pc-cta">
            <h3 className="font-display text-2xl md:text-3xl font-light">{c.cta_heading}</h3>
            <p className="text-sm text-ivory/70 mt-3 max-w-2xl leading-relaxed">{c.cta_text}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/partner-with-us#enquire" className="bg-ivory text-ink px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium inline-flex items-center gap-2 hover:bg-clay hover:text-white transition-colors" data-testid="pc-cta-assessment">
                <Handshake className="w-4 h-4" /> {c.cta_assessment}
              </Link>
              <button type="button" onClick={requestDownload} disabled={downloading}
                className="border border-ivory/40 px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium inline-flex items-center gap-2 hover:bg-ivory/10 transition-colors disabled:opacity-60" data-testid="pc-cta-download">
                <FileDown className="w-4 h-4" /> {downloading ? "Preparing…" : c.cta_download}
              </button>
              <Link to="/partner-with-us" className="border border-ivory/40 px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium inline-flex items-center gap-2 hover:bg-ivory/10 transition-colors" data-testid="pc-cta-speak">
                <Phone className="w-4 h-4" /> {c.cta_speak}
              </Link>
            </div>
          </div>
        </Reveal>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-w-2xl bg-ivory border-ink/10 max-h-[85vh] overflow-y-auto" data-testid="pc-detail-dialog">
          {detail && (() => {
            const md = cfg.models[detail];
            const op = calc.operator[detail][scenario];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl font-light">{md.name}</DialogTitle>
                  <DialogDescription className="text-sm text-graphite leading-relaxed">{md.desc}</DialogDescription>
                </DialogHeader>
                {detail === "management" && md.explain && <p className="text-xs text-graphite leading-relaxed bg-bone p-3">{md.explain}</p>}
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {SCENARIOS.map((s) => (
                    <div key={s} className={`border p-3 ${s === scenario ? "border-clay bg-white" : "border-ink/10 bg-white/60"}`}>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-graphite">{cfg.scenarios[s].label}</div>
                      <div className="font-display text-lg mt-1 tabular-nums" data-testid={`pc-detail-${detail}-${s}`}>{fmt(calc.ownerIncome[detail][s])}</div>
                      <div className="text-[10px] text-graphite">{perLabel}</div>
                    </div>
                  ))}
                </div>
                <div className="space-y-1.5 text-xs mt-2">
                  <div className="flex justify-between"><span className="text-graphite">{cfg.scenarios[scenario].label} revenue</span><span className="tabular-nums">{inr(calc.revs[scenario])}</span></div>
                  {detail === "management" && <div className="flex justify-between"><span className="text-graphite">Operating expenses you fund</span><span className="tabular-nums">− {inr(calc.opex[scenario])}</span></div>}
                  {detail === "management" && <div className="flex justify-between"><span className="text-graphite">Happy Retreats management fee</span><span className="tabular-nums">− {inr(calc.mgmtFee[scenario])}</span></div>}
                  {detail === "fixed" && <div className="flex justify-between"><span className="text-graphite">Agreed fixed rent (unaffected by performance)</span><span className="tabular-nums">{inr(calc.rent)}</span></div>}
                  {detail === "mg" && <div className="flex justify-between"><span className="text-graphite">Guaranteed minimum</span><span className="tabular-nums">{inr(calc.minGuarantee)}</span></div>}
                  {detail === "mg" && <div className="flex justify-between"><span className="text-graphite">Revenue share ({calc.sharePctEff}% of revenue)</span><span className="tabular-nums">{inr(calc.mgShareAmt[scenario])}</span></div>}
                  <div className="flex justify-between font-medium border-t border-ink/10 pt-1.5"><span>Your estimated income</span><span className="tabular-nums">{inr(calc.ownerIncome[detail][scenario])}</span></div>
                </div>
                <HowCalculated lines={calcLines(detail)} />
                {bands && detail !== "management" && (
                  <div className="mt-3 bg-bone p-4" data-testid={`pc-detail-years-${detail}`}>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-clay font-medium mb-2">How your offer grows over the years</div>
                    <table className="w-full text-xs">
                      <thead><tr className="text-graphite text-[10px] uppercase">
                        <th className="text-left py-1 font-medium">Period</th><th className="text-right py-1 font-medium">Offer %</th><th className="text-right py-1 font-medium">Estimated income /year</th>
                      </tr></thead>
                      <tbody>
                        {bands.map((b, i) => {
                          const yIncome = detail === "fixed" ? calc.R * b.pct / 100 : Math.max(calc.minGuarantee, calc.revs[scenario] * b.pct / 100);
                          return (
                            <tr key={i} className="border-t border-ink/10">
                              <td className="py-1.5">{bandLabel(bands, i)}</td>
                              <td className="py-1.5 text-right tabular-nums">{b.pct}%</td>
                              <td className="py-1.5 text-right tabular-nums font-medium" data-testid={`pc-year-income-${detail}-${i}`}>{inr(yIncome)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <p className="mt-2 text-[10px] text-graphite/80 leading-relaxed">{cfg.stages.readiness_note}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 text-xs mt-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 font-medium mb-2">{md.gains_title}</div>
                    <ul className="space-y-1">{md.gains.map((g, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">✓</span><span>{g}</span></li>)}</ul>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-graphite font-medium mb-2">{md.gives_title}</div>
                    <ul className="space-y-1 text-graphite">{md.gives.map((g, i) => <li key={i} className="flex gap-2"><span>·</span><span>{g}</span></li>)}</ul>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs mt-3 bg-bone p-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-graphite font-medium mb-2">You remain responsible for</div>
                    <ul className="space-y-1 text-graphite">{md.owner_responsibility.map((g, i) => <li key={i}>· {g}</li>)}</ul>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-graphite font-medium mb-2">Happy Retreats takes care of</div>
                    <ul className="space-y-1 text-graphite">{md.hr_responsibility.map((g, i) => <li key={i}>· {g}</li>)}</ul>
                  </div>
                </div>
                <p className="text-[10px] text-graphite/60 leading-relaxed mt-2">{c.disclaimer}</p>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      <GuestDetailsDialog
        open={guestOpen}
        onClose={() => setGuestOpen(false)}
        onSubmit={(g) => { setGuestOpen(false); downloadPdf(g); }}
        heading="Almost there — who is this comparison for?"
        ctaLabel="Get my comparison report"
      />
    </section>
  );
}
